import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { HiMenu, HiX } from "react-icons/hi";
import PropTypes from "prop-types";
import Button from "@components/Button";
import throttle from "lodash/throttle";
// import SplitText from "@components/SplitText";

export default function Navbar({ onLoad }) {
	const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
	const [isScrolled, setIsScrolled] = useState(false);
	const [isVisible, setIsVisible] = useState(true);
	const lastScrollY = useRef(0);
	const location = useLocation();

	useEffect(() => {
		const handleScroll = throttle(() => {
			const currentScrollY = window.scrollY;
			const isScrollingUp = currentScrollY < lastScrollY.current;

			if (isMobileMenuOpen) {
				setIsVisible(true);
			} else {
				if (currentScrollY < 20) {
					setIsVisible(true);
				} else if (isScrollingUp) {
					setIsVisible(true);
				} else {
					setIsVisible(false);
				}
			}

			setIsScrolled(currentScrollY > 20);
			lastScrollY.current = currentScrollY;
		}, 100);

		window.addEventListener("scroll", handleScroll);
		return () => {
			window.removeEventListener("scroll", handleScroll);
			handleScroll.cancel();
		};
	}, [isMobileMenuOpen]);

	const navLinks = [
		// { name: "Home", path: "/" },
		{ name: "Services", path: "/services" },
		{ name: "Packages", path: "/packages" },
		{ name: "About Us", path: "/about" },
		{ name: "Contact Us", path: "/contact" },
	];

	const mobileNavLinks = [
		// { name: "Home", path: "/" },
		{ name: "Services", path: "/services" },
		{ name: "Packages", path: "/packages" },
		{ name: "About Us", path: "/about" },
		{ name: "Contact Us", path: "/contact" },
		{ name: "Blogs", path: "/articles" },
	];

	const navVariants = {
		initial: { y: -100 },
		animate: {
			y: 0,
			transition: {
				type: "spring",
				stiffness: 100,
				damping: 20,
				mass: 1,
			},
		},
	};

	const mobileMenuVariants = {
		initial: { y: -20, opacity: 0 },
		animate: {
			y: 0,
			opacity: 1,
			transition: {
				type: "spring",
				stiffness: 100,
			},
		},
		exit: {
			y: -20,
			opacity: 0,
			transition: {
				type: "spring",
				stiffness: 100,
			},
		},
	};

	return (
		<>
			<motion.div
				className={`fixed top-0 left-0 right-0 z-50 lg:px-6 px-4 py-2 rounded-2xl transition-all ease-linear bg-steerOrange ${
					isScrolled ? "shadow-lg lg:mx-12 mx-4 my-4" : "lg:m-4 m-2"
				}`}
				variants={navVariants}
				initial="initial"
				animate={isVisible ? "animate" : "initial"}
			>
				<div className="flex justify-between items-center">
					{/* Logo */}
					<Link to="/" className="flex gap-2 items-center">
						<img
							src="/images/logo-transparentbg-white-notext.webp"
							alt="navbar-logo"
							className="md:w-12 w-10"
							width="48"
							height="48"
							onLoad={() => {
								if (onLoad) onLoad();
							}}
						/>

						<h2 className="md:text-2xl text-xl tracking-tight uppercase font-bold text-steerBlack flex flex-col gap-0">
							{/* <SplitText
								text="STEER SMART"
								className="md:text-2xl text-md tracking-wide uppercase font-bold text-steerBlack"
								delay={100}
								duration={0.6}
								ease="power3.out"
								splitType="chars"
								from={{ opacity: 0, y: 40 }}
								to={{ opacity: 1, y: 0 }}
								threshold={0.1}
								rootMargin="-100px"
								textAlign="center"
							/> */}
							STEER SMART
							<span className="flex text-xs text-steerBlack tracking-normal">
								Driving School
							</span>
						</h2>
					</Link>
					{/* Desktop Navigation Links */}
					<div className="hidden md:flex items-center justify-center flex-1">
						<div className="flex gap-8">
							{navLinks.map((link) => (
								<Link
									key={link.name}
									to={link.path}
									className={`text-white font-bespokeSans cursor-pointer uppercase ${
										location.pathname === link.path
											? "font-medium underline underline-offset-8"
											: "font-light hover:font-semibold duration-300 ease-in-out transition-all"
									}`}
								>
									{link.name}
								</Link>
							))}
						</div>
					</div>
					{/* Right Section */}
					<div className="flex items-center gap-4">
						<div className="md:flex hidden">
							<Button
								buttonName="Book Now"
								buttonLink="/packages"
								buttonStyle="primary"
							/>
						</div>

						{/* <div className="md:flex hidden">
							<Button
								buttonName="Contact Us"
								buttonLink="/contact"
								buttonStyle="primary"
							/>
						</div> */}

						<button
							className="md:hidden text-2xl transition-colors text-white hover:text-white/80"
							onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
							aria-label="Toggle mobile menu"
						>
							{isMobileMenuOpen ? <HiX /> : <HiMenu />}
						</button>
					</div>
				</div>
			</motion.div>

			{/* Mobile Menu */}
			<AnimatePresence>
				{isMobileMenuOpen && (
					<motion.div
						className="fixed inset-x-6 top-24 bg-steerOrange shadow-lg rounded-xl p-4 z-40 md:hidden"
						variants={mobileMenuVariants}
						initial="initial"
						animate="animate"
						exit="exit"
					>
						<div className="flex flex-col gap-4">
							{mobileNavLinks.map((link) => (
								<Link
									key={link.name}
									to={link.path}
									className={`text-white hover:text-white/80 font-bespokeSans uppercase ${
										location.pathname === link.path
											? "font-bold underline underline-offset-8"
											: "font-light"
									}`}
									onClick={() => setIsMobileMenuOpen(false)}
								>
									{link.name}
								</Link>
							))}
						</div>
					</motion.div>
				)}
			</AnimatePresence>
		</>
	);
}

Navbar.propTypes = {
	onLoad: PropTypes.func,
};
